/**
 * SidebarCustomizer — modal do personalizacji widoczności modułów w sidebar.
 *
 * Odczytuje wszystkie `.nav-btn` z sidebar aside i pozwala je włączać/wyłączać.
 * Preferencje synchronizowane cross-device: MongoDB (`db.users.$.uiPreferences.sidebarHidden`)
 * + lokalny cache w localStorage per użytkownik: `sidebar_hidden::{username}`.
 *
 * Zwraca: modal (portal) + hook `useSidebarVisibility(username)` który zwraca
 * Set<string> z ukrytymi identyfikatorami przycisków (testId lub tekst).
 */
import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '../../utils/constants';
import PortalOverlay from '../common/PortalOverlay';

const storageKey = (username) => `sidebar_hidden::${username || 'anon'}`;

const loadHidden = (username) => {
  try {
    const raw = localStorage.getItem(storageKey(username));
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch { return new Set(); }
};

const saveHidden = (username, set) => {
  try {
    localStorage.setItem(storageKey(username), JSON.stringify(Array.from(set)));
  } catch { /* quota */ }
};

// ---- Backend sync helpers ----
const fetchRemotePrefs = async (userId) => {
  if (!userId) return null;
  try {
    const r = await axios.get(`${API}/users/${userId}/ui-preferences`);
    return Array.isArray(r.data?.sidebarHidden) ? r.data.sidebarHidden : [];
  } catch { return null; }
};

const saveRemotePrefs = async (userId, hiddenSet) => {
  if (!userId) return false;
  try {
    await axios.patch(`${API}/users/${userId}/ui-preferences`, {
      sidebarHidden: Array.from(hiddenSet),
    });
    return true;
  } catch { return false; }
};

const buttonKey = (btn) => {
  const testid = btn.getAttribute('data-testid');
  if (testid) return testid;
  // Fallback: użyj textContent oczyszczonego z dynamicznych badge/liczb/spacji
  // żeby klucz był stabilny gdy licznik powiadomień się zmienia
  const clone = btn.cloneNode(true);
  clone.querySelectorAll('style, script, .unread-dot, [class*="badge"], [data-testid*="badge"], [data-testid*="dot"]').forEach((el) => el.remove());
  const text = (clone.textContent || '')
    .replace(/\d+/g, '')       // usuń wszystkie cyfry (liczniki)
    .replace(/\s+/g, ' ')      // znormalizuj whitespace
    .trim();
  return text;
};

export const useSidebarVisibility = (username, asideRef, userId = null) => {
  const [hidden, setHidden] = useState(() => loadHidden(username));
  const remoteSyncedRef = useRef(false);

  // Reload gdy zmienia się user
  useEffect(() => {
    setHidden(loadHidden(username));
    remoteSyncedRef.current = false;
  }, [username]);

  // Pull remote preferences once per userId (nadpisze localStorage jeśli różne)
  useEffect(() => {
    if (!userId || remoteSyncedRef.current) return;
    remoteSyncedRef.current = true;
    (async () => {
      const remote = await fetchRemotePrefs(userId);
      if (Array.isArray(remote)) {
        const remoteSet = new Set(remote);
        saveHidden(username, remoteSet);
        setHidden(remoteSet);
        window.dispatchEvent(new Event('sidebar-visibility-changed'));
      }
    })();
  }, [userId, username]);

  // Nasłuchuj event z modala żeby przeładować z localStorage
  useEffect(() => {
    const reload = () => setHidden(loadHidden(username));
    window.addEventListener('sidebar-visibility-changed', reload);
    window.addEventListener('storage', reload);
    return () => {
      window.removeEventListener('sidebar-visibility-changed', reload);
      window.removeEventListener('storage', reload);
    };
  }, [username]);

  // Aplikuj widoczność do DOM za każdym renderem sidebaru
  useLayoutEffect(() => {
    if (!asideRef?.current) return;
    const buttons = asideRef.current.querySelectorAll('.nav-btn');
    const validKeys = new Set();
    buttons.forEach((btn) => {
      const k = buttonKey(btn);
      validKeys.add(k);
      if (hidden.has(k)) {
        btn.dataset.sidebarHidden = 'true';
        btn.style.display = 'none';
      } else {
        if (btn.dataset.sidebarHidden === 'true') {
          delete btn.dataset.sidebarHidden;
          btn.style.display = '';
        }
      }
    });
    // Wyczyść stare wpisy z localStorage które nie pasują do żadnego istniejącego przycisku
    const stale = Array.from(hidden).filter((k) => !validKeys.has(k));
    if (stale.length > 0) {
      const cleaned = new Set(Array.from(hidden).filter((k) => validKeys.has(k)));
      saveHidden(username, cleaned);
      setHidden(cleaned);
    }
  }, [hidden, asideRef, username]);

  const updateHidden = useCallback((next) => {
    const newSet = new Set(next);
    setHidden(newSet);
    saveHidden(username, newSet);
  }, [username]);

  return [hidden, updateHidden];
};

/**
 * SidebarCustomizerModal — modal do zaznaczania które moduły widzieć.
 */
export const SidebarCustomizerModal = ({ isOpen, onClose, asideRef, username, userId = null }) => {
  const [buttons, setButtons] = useState([]);
  const [hidden, setHidden] = useState(() => loadHidden(username));
  const [syncing, setSyncing] = useState(false);

  // Odczytaj wszystkie nav-btn gdy modal się otwiera
  useEffect(() => {
    if (!isOpen || !asideRef?.current) return;
    const nodes = asideRef.current.querySelectorAll('.nav-btn');
    const items = Array.from(nodes).map((btn) => {
      const key = buttonKey(btn);
      // Nowy sidebar używa Lucide SVG (.nav-icon-svg); fallback do starego .nav-icon (emoji tekst)
      const svgEl = btn.querySelector('.nav-icon-svg');
      const iconSvg = svgEl ? svgEl.outerHTML : null;
      const iconText = btn.querySelector('.nav-icon')?.textContent?.trim() || '';
      // Klonuj i wyczyść ikony/style/badge żeby label był tylko tekstem
      const clone = btn.cloneNode(true);
      clone.querySelectorAll('style, script, svg, .unread-dot, [data-testid*="badge"], .nav-icon, .nav-icon-svg').forEach((el) => el.remove());
      const label = (clone.textContent || '').replace(/\s+/g, ' ').trim();
      return { key, iconSvg, iconText, label };
    }).filter((it) => it.key && it.label);
    setButtons(items);
    setHidden(loadHidden(username));
  }, [isOpen, asideRef, username]);

  if (!isOpen) return null;

  const persist = async (next) => {
    setHidden(next);
    saveHidden(username, next);
    window.dispatchEvent(new Event('sidebar-visibility-changed'));
    if (userId) {
      setSyncing(true);
      await saveRemotePrefs(userId, next);
      setSyncing(false);
    }
  };

  const toggle = (key) => {
    const next = new Set(hidden);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    persist(next);
  };

  const showAll = () => {
    persist(new Set());
  };

  const visibleCount = buttons.length - hidden.size;

  return (
    <PortalOverlay onClose={onClose}>
      <div
        onClick={onClose}
        data-testid="sidebar-customizer-overlay"
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.75)',
          zIndex: 2147483000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 20,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'var(--panel-bg, #1a1a1a)',
            border: '1px solid var(--border, #333)',
            borderRadius: 14,
            width: 'min(560px, 100%)', maxHeight: '85vh',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          }}
        >
          {/* Header */}
          <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text)' }}>⚙️ Personalizacja menu</h3>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
                <strong style={{ color: 'var(--gold)' }}>{visibleCount}</strong> z {buttons.length} modułów widocznych
              </p>
            </div>
            <button
              onClick={onClose}
              data-testid="sidebar-customizer-close"
              style={{
                padding: '6px 12px', borderRadius: 8, background: 'transparent',
                color: 'var(--text-muted)', border: '1px solid var(--border)',
                cursor: 'pointer', fontSize: 12, fontWeight: 600,
              }}
            >
              ✕ Zamknij
            </button>
          </div>

          {/* List */}
          <div style={{ overflowY: 'auto', flex: 1, padding: '10px 8px' }}>
            {buttons.map((btn) => {
              const isHidden = hidden.has(btn.key);
              return (
                <label
                  key={btn.key}
                  data-testid={`sidebar-cust-item-${btn.key}`}
                  onClick={() => toggle(btn.key)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                    background: isHidden ? 'transparent' : 'rgba(34,197,94,0.06)',
                    border: `1px solid ${isHidden ? 'var(--border)' : 'rgba(34,197,94,0.3)'}`,
                    marginBottom: 4,
                    transition: 'all 120ms',
                    opacity: isHidden ? 0.55 : 1,
                  }}
                >
                  <span
                    style={{
                      width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      border: `1.5px solid ${isHidden ? 'rgba(148,163,184,0.5)' : '#22c55e'}`,
                      background: isHidden ? 'transparent' : '#22c55e',
                    }}
                  >
                    {!isHidden && (
                      <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                        <path d="M2.5 6.2 L5 8.5 L9.5 3.8" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </span>
                  <span style={{ width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--gold, #d4af37)', opacity: isHidden ? 0.5 : 1 }}>
                    {btn.iconSvg ? (
                      <span dangerouslySetInnerHTML={{ __html: btn.iconSvg }} />
                    ) : (
                      <span style={{ fontSize: 18 }}>{btn.iconText || '•'}</span>
                    )}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', flex: 1 }}>{btn.label}</span>
                  <span style={{ fontSize: 11, color: isHidden ? '#ef4444' : '#22c55e', fontWeight: 700 }}>
                    {isHidden ? 'Ukryty' : 'Widoczny'}
                  </span>
                </label>
              );
            })}
          </div>

          {/* Footer */}
          <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
            <button
              onClick={showAll}
              data-testid="sidebar-customizer-show-all"
              style={{
                padding: '8px 14px', borderRadius: 8, background: 'transparent',
                color: 'var(--gold)', border: '1px solid var(--gold)',
                cursor: 'pointer', fontSize: 12, fontWeight: 700,
              }}
            >
              ↻ Pokaż wszystkie
            </button>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {syncing ? '☁️ Synchronizuję…' : '☁️ Zapisywane w chmurze'}
            </span>
          </div>
        </div>
      </div>
    </PortalOverlay>
  );
};

export default SidebarCustomizerModal;
